import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {NavStoreSource} from 'constants/SourceConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ErrorHandlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AllSourcesQuery} from 'nav/graphql/__generated/AllSourcesQuery';
import {ALL_SOURCES_QUERY} from 'nav/graphql/AllSources';
import {BaseParentStorableDataSelector} from 'samples/ParentStorableDataSelector';
import SchemaSelector from 'samples/SchemaSelector';
import {
  BoxSchema,
  ContainerSchema,
  PlateSchema,
  StorableSchema,
} from 'samples/storables/StorableSchemaConstants';
import {Schema} from 'schemas/SchemaConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import SourceSelector from 'ui-library/SourceSelector';
import APIUtil from 'util/APIUtil';

import {StorableSummaryGraphQL} from '../../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {
  AutomationOutputFileDestinationInfo,
  DestinationInfoAction,
  TransformationFieldInfo,
  TransformationFieldType,
} from '../RunSchemaSpecConstants';
import {
  DestinationInfoModalQuery,
  DestinationInfoModalQueryVariables,
} from './__generated/DestinationInfoModalQuery';
import {DestinationInfoModalSchemasQuery} from './__generated/DestinationInfoModalSchemasQuery';
import SchemaInfoFieldsSection from './SchemaInfoFieldsSection';

export const DESTINATION_INFO_MODAL_QUERY = gql`
  query DestinationInfoModalQuery($locationId: StrictString!) @skipAudit @engComponent(name: LAB_AUTOMATION) {
    location(id: $locationId) {
      ... on LocationNode {
        ...LocationGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Location').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface BaseDestinationInfoModalProps {
  initialDestinationInfo: AutomationOutputFileDestinationInfo;
  initialActionType: DestinationInfoAction | undefined;
  onRequestClose: () => void;
  onRequestSubmit: (destinationInfo: AutomationOutputFileDestinationInfo | null) => void;
}

interface ConnectedProps {
  initialLocation: StorableSummaryGraphQL | null;
  sources: Array<NavStoreSource>;
  areSourcesLoading: boolean;
}

const actionTypeOptions: {[key in DestinationInfoAction]: string} = {
  [DestinationInfoAction.CREATE_OR_UPDATE]: 'Create new destinations',
  [DestinationInfoAction.USE_EXISTING]: 'Use existing destinations',
};

const inventoryTypeOptions: Partial<Record<ModelTypeConstants, string>> = {
  [ModelTypeConstants.CONTAINER_SCHEMA]: 'Container',
  [ModelTypeConstants.PLATE_SCHEMA]: 'Plate',
  [ModelTypeConstants.BOX_SCHEMA]: 'Box',
};

const schemaKeys: {[key: string]: 'containerSchema' | 'boxSchema' | 'plateSchema'} = {
  [ModelTypeConstants.CONTAINER_SCHEMA]: 'containerSchema',
  [ModelTypeConstants.PLATE_SCHEMA]: 'plateSchema',
  [ModelTypeConstants.BOX_SCHEMA]: 'boxSchema',
};

export const DESTINATION_INFO_MODAL_SCHEMAS_QUERY = gql`
  query DestinationInfoModalSchemasQuery @skipAudit @engComponent(name: LAB_AUTOMATION) {
    ...AllBoxSchemasGraphQL
    ...AllContainerSchemasGraphQL
    ...AllPlateSchemasGraphQL
  }
  ${getFragmentWrapper('AllBoxSchemas').fragment}
  ${getFragmentWrapper('AllContainerSchemas').fragment}
  ${getFragmentWrapper('AllPlateSchemas').fragment}
`;

export function DestinationInfoModal(props: BaseDestinationInfoModalProps & ConnectedProps): JSX.Element {
  const schemasQueryResult = useBenchlingQuery<DestinationInfoModalSchemasQuery>(
    DESTINATION_INFO_MODAL_SCHEMAS_QUERY
  );
  const [destinationInfo, setDestinationInfo] = useState(props.initialDestinationInfo);
  const [actionType, setActionType] = useState(props.initialActionType);
  const [location, setLocation] = useState(props.initialLocation);
  useEffect(() => {
    setLocation(props.initialLocation);
  }, [props.initialLocation]);
  const selectedSchemaId =
    destinationInfo.containerSchema || destinationInfo.plateSchema || destinationInfo.boxSchema;
  const [inventoryType, setInventoryType] = useState(
    selectedSchemaId ? APIUtil.apiIdToMaybeType(selectedSchemaId) : null
  );
  let allContainerSchemas: Array<ContainerSchema> = [];
  let allPlateSchemas: Array<PlateSchema> = [];
  let allBoxSchemas: Array<BoxSchema> = [];
  let selectedSchema: StorableSchema | null = null;
  if (schemasQueryResult.type === QueryResultType.SUCCESS) {
    allContainerSchemas = graphQLArrayToREST('ContainerSchema', schemasQueryResult.data.containerSchemas);
    allPlateSchemas = graphQLArrayToREST('PlateSchema', schemasQueryResult.data.plateSchemas);
    allBoxSchemas = graphQLArrayToREST('BoxSchema', schemasQueryResult.data.boxSchemas);
    if (destinationInfo.containerSchema) {
      selectedSchema =
        allContainerSchemas.find((schema) => schema.id === destinationInfo.containerSchema) ?? null;
    } else if (destinationInfo.plateSchema) {
      selectedSchema = allPlateSchemas.find((schema) => schema.id === destinationInfo.plateSchema) ?? null;
    } else if (destinationInfo.boxSchema) {
      selectedSchema = allBoxSchemas.find((schema) => schema.id === destinationInfo.boxSchema) ?? null;
    }
  }

  const schemasByInventoryType: {[key: string]: Array<Schema>} = {
    [ModelTypeConstants.CONTAINER_SCHEMA]: allContainerSchemas,
    [ModelTypeConstants.PLATE_SCHEMA]: allPlateSchemas,
    [ModelTypeConstants.BOX_SCHEMA]: allBoxSchemas,
  };

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        if (actionType === DestinationInfoAction.USE_EXISTING) {
          props.onRequestSubmit(null);
        } else {
          props.onRequestSubmit(destinationInfo);
        }
      }}
      title="Configure destinations"
      buttons={[
        {
          text: 'Done',
          submit: true,
          style: ButtonStyle.SECONDARY,
          // TODO better validation
          disabled:
            actionType === DestinationInfoAction.CREATE_OR_UPDATE
              ? selectedSchema == null
              : actionType == null,
        },
      ]}
    >
      {schemasQueryResult.type === QueryResultType.SUCCESS ? (
        <>
          <FieldLabel label="Action type" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <br />
          <SelectDropdown
            displayName={actionType ? actionTypeOptions[actionType] : 'Select'}
            menu={
              <Menu
                value={actionType}
                height={Menu.HEIGHTS.MEDIUM}
                width={Menu.WIDTHS.FULL}
                scrollable={true}
              >
                {Object.entries(actionTypeOptions).map(
                  ([value, display]: [DestinationInfoAction, string]) => (
                    <Option
                      key={value}
                      value={value}
                      onSelect={() => {
                        setActionType(value);
                      }}
                    >
                      {display}
                    </Option>
                  )
                )}
              </Menu>
            }
          />
          {actionType === DestinationInfoAction.CREATE_OR_UPDATE ? (
            <>
              <div className={css(styles.row)}>
                <div className={css(styles.inventoryTypeSelector)}>
                  <FieldLabel label="Inventory type" labelStyle={LabelStyle.LABEL} isRequired={true} />
                  <br />
                  <SelectDropdown
                    displayName={inventoryType ? inventoryTypeOptions[inventoryType] : 'Select'}
                    menu={
                      <Menu
                        value={inventoryType}
                        height={Menu.HEIGHTS.MEDIUM}
                        width={Menu.WIDTHS.FULL}
                        scrollable={true}
                      >
                        {Object.entries(inventoryTypeOptions).map(
                          ([value, display]: [ModelTypeConstants, string]) => (
                            <Option
                              key={value}
                              value={value}
                              onSelect={() => {
                                setInventoryType(value);
                              }}
                            >
                              {display}
                            </Option>
                          )
                        )}
                      </Menu>
                    }
                  />
                </div>
                <div className={css(styles.schemaSelector)}>
                  <FieldLabel label="Schema" labelStyle={LabelStyle.LABEL} isRequired={true} />
                  <br />
                  <SchemaSelector
                    isDisabled={inventoryType == null}
                    onChange={(schema: Schema) => {
                      const newDestinationInfo = {...destinationInfo, fields: {}};
                      for (const schemaKey of Object.values(schemaKeys)) {
                        delete newDestinationInfo[schemaKey];
                      }
                      const schemaKey = schemaKeys[inventoryType!];
                      newDestinationInfo[schemaKey] = schema.id;
                      setDestinationInfo(newDestinationInfo);
                    }}
                    schemas={inventoryType ? schemasByInventoryType[inventoryType] : []}
                    selectedSchemaId={selectedSchemaId}
                  />
                </div>
              </div>
              <div className={css(styles.row)}>
                <div className={css(styles.locationSelector)}>
                  <BaseParentStorableDataSelector
                    label="Default location"
                    isRequired={false}
                    storableSchema={selectedSchema}
                    parentStorableTypesOverride={[ModelTypeConstants.LOCATION]}
                    selectedParent={location ? {storable: location, gridCells: []} : undefined}
                    onSelect={(selectedParent) => {
                      const location = selectedParent.storable;
                      setLocation(location);
                      if (location) {
                        setDestinationInfo({...destinationInfo, location: location.id});
                      } else {
                        const newDestinationInfo = {...destinationInfo};
                        delete newDestinationInfo.location;
                        setDestinationInfo(newDestinationInfo);
                      }
                    }}
                    onRemove={() => {
                      setLocation(null);
                      const newDestinationInfo = {...destinationInfo};
                      delete newDestinationInfo.location;
                      setDestinationInfo(newDestinationInfo);
                    }}
                  />
                </div>
                <div className={css(styles.projectSelector)}>
                  <FieldLabel label="Default project" labelStyle={LabelStyle.LABEL} />
                  <br />
                  <SourceSelector
                    sources={props.sources}
                    areSourcesLoading={props.areSourcesLoading}
                    selectedSourceId={destinationInfo.project ?? null}
                    onSourceSelected={(projectId) => {
                      if (projectId != null) {
                        setDestinationInfo({...destinationInfo, project: projectId});
                      } else {
                        const newDestinationInfo = {...destinationInfo};
                        delete newDestinationInfo.project;
                        setDestinationInfo(newDestinationInfo);
                      }
                    }}
                    allowNone={true}
                    enableRedesign={true}
                    showRegistries={true}
                  />
                </div>
              </div>
              <SchemaInfoFieldsSection
                schema={selectedSchema}
                fields={destinationInfo.fields}
                allTagSchemas={[]}
                allFieldsets={[]}
                allowedFieldTypes={[TransformationFieldType.DIRECT]}
                onUpdateField={(fieldKey: string, fieldInfo: TransformationFieldInfo | null) => {
                  const newFields = {...destinationInfo.fields};
                  if (fieldInfo) {
                    newFields[fieldKey] = fieldInfo;
                  } else {
                    delete newFields[fieldKey];
                  }
                  setDestinationInfo({...destinationInfo, fields: newFields});
                }}
              />
            </>
          ) : null}
        </>
      ) : (
        <CenteredHorizontalThrobber />
      )}
    </Modal>
  );
}

export default function QueryingDestinationInfoModal(props: BaseDestinationInfoModalProps): JSX.Element {
  return (
    <ErrorHandlingQuery<AllSourcesQuery> query={ALL_SOURCES_QUERY}>
      {({data: sourcesData, loading: areSourcesLoading}) => {
        const commonProps = {
          ...props,
          sources: sourcesData ? graphQLArrayToREST('NavStoreSource', sourcesData.sources) : [],
          areSourcesLoading,
        };
        if (props.initialDestinationInfo.location != null) {
          return (
            <ErrorHandlingQuery<DestinationInfoModalQuery, DestinationInfoModalQueryVariables>
              query={DESTINATION_INFO_MODAL_QUERY}
              fetchPolicy="cache-and-network"
              variables={{
                locationId: props.initialDestinationInfo.location,
              }}
            >
              {({data}) => (
                <DestinationInfoModal
                  {...commonProps}
                  initialLocation={data && !isInaccessible(data.location) ? data.location : null}
                />
              )}
            </ErrorHandlingQuery>
          );
        }
        return <DestinationInfoModal {...commonProps} initialLocation={null} />;
      }}
    </ErrorHandlingQuery>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: '20px',
  },
  inventoryTypeSelector: {
    flex: 0.5,
    marginRight: '40px',
  },
  schemaSelector: {
    flex: 0.5,
  },
  projectSelector: {
    flex: 0.5,
  },
  locationSelector: {
    flex: 0.5,
    marginRight: '40px',
  },
});
